// Tiny 140×60 chart thumbnail rendered on each Explore › Stats hub tile
// (#235). Switches on `kind` from the catalogue and renders a stripped-down
// Recharts sparkline (no axes/labels/tooltip/grid) — or for `card` kind, a
// single big stat number — using the page's preview payload from
// `/api/explore/stats/hub-previews`.
//
// Designed to be cheap to render (no animation, no axes) and graceful when
// the preview is missing/empty (renders an empty placeholder box).

import React from 'react';
import { Box, Skeleton, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import {
  ResponsiveContainer,
  LineChart, Line,
  BarChart, Bar,
} from 'recharts';

const THUMB_W = '100%';
const THUMB_H = 60;

// Format the value text inside a `card` kind thumbnail. Big-ish but not
// huge — the tile title sits above it.
function fmtCardValue(v) {
  if (v == null) return '—';
  if (typeof v === 'number') {
    if (!Number.isFinite(v)) return '—';
    if (Math.abs(v) >= 1000) return v.toLocaleString();
    if (Number.isInteger(v)) return String(v);
    return String(Math.round(v * 10) / 10);
  }
  return String(v);
}

function ThumbWrap({ children }) {
  return (
    <Box sx={{ width: THUMB_W, height: THUMB_H,
                display: 'flex', alignItems: 'center',
                justifyContent: 'center' }}>
      {children}
    </Box>
  );
}

export default function StatThumbnail({ preview, kind, loading }) {
  const theme = useTheme();
  const accent = theme.palette.primary.main;
  const accent2 = theme.palette.secondary?.main || theme.palette.info?.main
                     || theme.palette.success?.main || '#1565C0';

  if (loading) {
    return (
      <Skeleton variant="rounded"
                animation="wave"
                sx={{ width: THUMB_W, height: THUMB_H,
                      borderRadius: 1 }} />
    );
  }

  // No data / no preview — empty placeholder (silent fallback).
  if (!preview) {
    return <ThumbWrap><Box sx={{ width: THUMB_W, height: THUMB_H }} /></ThumbWrap>;
  }

  const effectiveKind = kind || preview.kind || 'card';

  if (effectiveKind === 'card') {
    return (
      <ThumbWrap>
        <Box sx={{ textAlign: 'center', width: '100%' }}>
          <Typography variant="h5" component="div"
                       sx={{ color: 'primary.main', lineHeight: 1.1,
                              fontWeight: 600 }}>
            {fmtCardValue(preview.value)}
          </Typography>
          {preview.label ? (
            <Typography variant="caption" color="text.secondary"
                         sx={{ display: 'block',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                                px: 0.5 }}>
              {preview.label}
            </Typography>
          ) : null}
        </Box>
      </ThumbWrap>
    );
  }

  const points = preview.points || [];
  if (!points.length) {
    return <ThumbWrap><Box sx={{ width: THUMB_W, height: THUMB_H }} /></ThumbWrap>;
  }

  // Stacked-bar thumbnail: render up to 2 series stacked (catalogue stacked
  // pages currently use Women/Men or Agreed/Negatived).
  if (effectiveKind === 'stacked') {
    const keys = preview.keys && preview.keys.length
        ? preview.keys
        : Object.keys(points[0]).filter((k) => k !== 'x');
    return (
      <ThumbWrap>
        <ResponsiveContainer width="100%" height={THUMB_H}>
          <BarChart data={points}
                     margin={{ top: 2, right: 2, bottom: 2, left: 2 }}>
            {keys.slice(0, 4).map((k, i) => (
              <Bar key={k} dataKey={k} stackId="t"
                   fill={i === 0 ? accent : accent2}
                   isAnimationActive={false} />
            ))}
          </BarChart>
        </ResponsiveContainer>
      </ThumbWrap>
    );
  }

  if (effectiveKind === 'bar' || effectiveKind === 'sparkbar') {
    return (
      <ThumbWrap>
        <ResponsiveContainer width="100%" height={THUMB_H}>
          <BarChart data={points}
                     margin={{ top: 2, right: 2, bottom: 2, left: 2 }}>
            <Bar dataKey="y" fill={accent} isAnimationActive={false} />
          </BarChart>
        </ResponsiveContainer>
      </ThumbWrap>
    );
  }

  // Default — line sparkline.
  return (
    <ThumbWrap>
      <ResponsiveContainer width="100%" height={THUMB_H}>
        <LineChart data={points}
                    margin={{ top: 2, right: 2, bottom: 2, left: 2 }}>
          <Line dataKey="y" stroke={accent}
                strokeWidth={2} dot={false}
                isAnimationActive={false} />
        </LineChart>
      </ResponsiveContainer>
    </ThumbWrap>
  );
}
